import { useRef, useEffect, useState } from 'react';

const HeatmapOverlay = ({ signalQuality, landmarks, scanPhase, heartRate }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const [pulse, setPulse] = useState(0.5);

  // DEBUG: Log landmarks structure once
  useEffect(() => {
    if (landmarks && landmarks.points && landmarks.points.length > 0) {
      console.log('LANDMARK DEBUG:');
      console.log('Total points:', landmarks.points.length);
      console.log('First 5 points:', landmarks.points.slice(0, 5));
      console.log('Forehead indices:', landmarks.forehead);
      console.log('Sample forehead point [idx=151]:', landmarks.points[151]);
    }
  }, [landmarks]);

  // Pulse animation (heartbeat or searching)
  useEffect(() => {
    if (['detecting', 'scanning', 'accumulating', 'measuring', 'complete'].includes(scanPhase)) {
      const animate = () => {
        let bpm = heartRate || 60; // Default to 60 if searching
        if (scanPhase === 'scanning' || scanPhase === 'accumulating') {
          bpm = 30; // Slower breathing pulse during accumulation
        }

        const beatDuration = 60 / bpm;
        const now = Date.now() / 1000;
        const phase = (now % beatDuration) / beatDuration;
        setPulse(0.6 + Math.sin(phase * Math.PI * 2) * 0.3);
        animationRef.current = requestAnimationFrame(animate);
      };
      animate();
      return () => cancelAnimationFrame(animationRef.current);
    }
  }, [scanPhase, heartRate]);

  // Main draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const parent = canvas.parentElement;
    if (parent) {
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    }

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (['scanning', 'accumulating', 'measuring', 'complete'].includes(scanPhase)) {
      drawHeatmap(ctx, canvas);
    }
  });

  const drawHeatmap = (ctx, canvas) => {
    if (!landmarks || !landmarks.points) {
      // Waiting for landmarks
      return;
    }

    ctx.shadowBlur = 0;
    const w = canvas.width;
    const h = canvas.height;

    // FIRST: Draw ALL landmark points as small green dots for debugging
    ctx.fillStyle = 'lime';
    landmarks.points.forEach(([normX, normY], i) => {
      const px = normX * w;
      const py = normY * h;
      ctx.fillRect(px - 1, py - 1, 2, 2);

      // Label first few points
      if (i < 5) {
        ctx.fillStyle = 'yellow';
        ctx.font = '10px Arial';
        ctx.fillText(i, px + 3, py);
        ctx.fillStyle = 'lime';
      }
    });

    // SECOND: Draw face-mapped ROI blobs using REAL landmarks
    const regions = [
      { name: 'forehead', indices: landmarks.forehead, radius: 80, color: 'cyan' },
      { name: 'left_cheek', indices: landmarks.left_cheek, radius: 60, color: 'magenta' },
      { name: 'right_cheek', indices: landmarks.right_cheek, radius: 60, color: 'orange' }
    ];

    regions.forEach(region => {
      if (!region.indices || region.indices.length === 0) {
        return;
      }

      // Calculate ROI center from actual landmark points
      let sumX = 0, sumY = 0, count = 0;
      region.indices.forEach(idx => {
        if (idx >= 0 && idx < landmarks.points.length) {
          const [normX, normY] = landmarks.points[idx];
          sumX += normX * w;
          sumY += normY * h;
          count++;
        }
      });

      if (count === 0) {
        return;
      }

      const cx = sumX / count;
      const cy = sumY / count;

      // Create pulsing radial gradient
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, region.radius);

      const intensity = pulse;
      if (intensity < 0.5) {
        grad.addColorStop(0, `rgba(0, 200, 255, 0.7)`);
        grad.addColorStop(1, 'rgba(0, 100, 255, 0)');
      } else {
        grad.addColorStop(0, `rgba(255, 100, 0, 0.8)`);
        grad.addColorStop(1, 'rgba(255, 0, 0, 0)');
      }

      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, region.radius, 0, Math.PI * 2);
      ctx.fill();
    });

    // Heart indicator - Only show when we have a rate
    if (heartRate) {
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(10, 10, 180, 60);
      ctx.fillStyle = 'white';
      ctx.font = 'bold 24px monospace';
      ctx.fillText(`❤ ${Math.round(heartRate)} bpm`, 20, 45);

      const dotSize = 8 + pulse * 8;
      ctx.fillStyle = `rgba(255, ${Math.floor(100 + pulse * 155)}, ${Math.floor(100 + pulse * 155)}, ${pulse})`;
      ctx.beginPath();
      ctx.arc(160, 37, dotSize, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Show status instead
      ctx.fillStyle = 'rgba(0,0,0,0.6)';
      ctx.fillRect(10, 10, 240, 40);
      ctx.fillStyle = 'white';
      ctx.font = '16px monospace';
      ctx.fillText(`Scanner Active...`, 20, 35);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10
      }}
    />
  );
};

export default HeatmapOverlay;
